import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import SEO from "@/components/base/SEO";
import SchemaOrg from "@/components/base/SchemaOrg";
import Breadcrumb from "@/components/base/Breadcrumb";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/feature/ArticleCard";
import { getArticleById, getRelatedArticles } from "@/mocks/articles";
import { getCategoryById } from "@/mocks/categories";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block hover:text-primary-600">返回首页</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const category = getCategoryById(article.categoryId);
  const relatedArticles = getRelatedArticles(article);
  const pageTitle = `${article.title} - 黑料吃瓜网`;
  const pageDesc = article.summary;
  const pageKeywords = article.tags.join(",");

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`https://heiliachigua.com/article/${article.id}`}
        ogImage={article.coverImage}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.summary,
          image: article.coverImage,
          datePublished: article.publishDate,
          dateModified: article.publishDate,
          author: {
            "@type": "Person",
            name: article.author,
          },
          publisher: {
            "@type": "Organization",
            name: "黑料吃瓜网",
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://heiliachigua.com/article/${article.id}`,
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article className="max-w-4xl mx-auto px-4 md:px-6 pt-6 pb-12">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-600 font-medium hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
                {article.isBreaking && (
                  <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                    重磅爆料
                  </span>
                )}
                {article.isHot && (
                  <span className="text-xs px-3 py-1 rounded-full bg-accent-500 text-background-50 font-medium whitespace-nowrap">
                    热门
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount} 阅读
                </span>
              </div>
            </header>

            <figure className="mb-8 overflow-hidden rounded-lg">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </figure>

            <div
              className="prose prose-lg max-w-none prose-headings:text-foreground-950 prose-headings:font-heading prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4 prose-a:text-primary-500"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 font-medium">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-4xl mx-auto px-4 md:px-6 pb-12">
              <div className="border-t border-background-200 pt-8">
                <h2 className="text-xl font-bold text-foreground-950 font-heading mb-6">
                  📖 相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}